import { useState, useCallback, useEffect } from "react";
import toast from "react-hot-toast";
import { useAddDiscountMutation } from "@/store/api/orderApi";

interface OrderItem {
  product_id: string | undefined;
  category_id: number | undefined;
  quantity: number;
  price_set: number;
  discount_set: number;
  branch_id: string | null;
  options: { name: string; quantity: number }[];
  selected_appointment_date_time?: Date;
  appointment_duration?: number;
  selected_service_provider?: { id: number; name: string };
}

interface UseDiscountCodeProps {
  branchId: string | null;
  orderItems: OrderItem[];
  showCheckout: boolean;
}

export function useDiscountCode({
  branchId,
  orderItems,
  showCheckout,
}: UseDiscountCodeProps) {
  const [discountCode, setDiscountCode] = useState("");
  const [discountValue, setDiscountValue] = useState(0);
  const [promotionId, setPromotionId] = useState<number | undefined>(undefined);
  const [promotionName, setPromotionName] = useState<string | undefined>(
    undefined,
  );
  const [applyDiscountLoading, setApplyDiscountLoading] = useState(false);

  const [addDiscount] = useAddDiscountMutation();

  const applyDiscount = useCallback(async () => {
    if (!branchId) return;

    setApplyDiscountLoading(true);
    try {
      const res = await addDiscount({
        branch_id: branchId,
        cart_items: orderItems,
        discount_code: discountCode,
      }).unwrap();

      const {
        discount_value,
        message,
        discount_code,
        promotion_id,
        promotion_name,
      } = res;

      setDiscountValue(parseFloat(discount_value));
      setDiscountCode(discount_code);
      setPromotionId(promotion_id);
      setPromotionName(promotion_name);
      toast.success(message);
    } catch (error: any) {
      console.log("Error:", error);
      toast.error(error.data?.error || "Failed to apply the best discount.");
    } finally {
      setApplyDiscountLoading(false);
    }
  }, [branchId, orderItems, discountCode, addDiscount]);

  // Auto-apply best discount when checkout is shown
  useEffect(() => {
    const autoApplyDiscount = async () => {
      if (!branchId || !showCheckout) return;
      setApplyDiscountLoading(true);
      try {
        const res = await addDiscount({
          branch_id: branchId,
          cart_items: orderItems,
        }).unwrap();

        const {
          discount_value,
          discount_code,
          message,
          promotion_id,
          promotion_name,
        } = res;

        setDiscountCode(discount_code);
        setDiscountValue(parseFloat(discount_value));
        setPromotionId(promotion_id);
        setPromotionName(promotion_name);
        toast.success(message);
      } catch (error: any) {
        console.log("Error:", error);
      } finally {
        setApplyDiscountLoading(false);
      }
    };

    const debounceTimeout = setTimeout(() => {
      autoApplyDiscount();
    }, 500);

    return () => clearTimeout(debounceTimeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branchId, showCheckout]);

  const resetDiscount = useCallback(() => {
    setDiscountCode("");
    setDiscountValue(0);
    setPromotionId(undefined);
    setPromotionName(undefined);
  }, []);

  return {
    discountCode,
    discountValue,
    promotionId,
    promotionName,
    applyDiscountLoading,
    setDiscountCode,
    applyDiscount,
    resetDiscount,
  };
}
